import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import { validate as validateUUID } from 'uuid';

import { call } from '@spacefill/shared/src/utils/Backend';

import { UPDATING_RULES_SEARCH_URL } from '../pages/automations/AutomaticDataCompletion/constants';
import { useReloadPageAndClearStorageError } from '../utils/contextSwitch';

function getTabLabels(t) {
    return {
        packaging: t('Packing'),
        details: t('Details'),
        documents: t('Documents'),
        activities: t('Activities'),
        incidents: t('Incidents'),
        integrations: t('Integrations'),
        appointments: t('Appointments'),
        stops: t('Stops'),
        stocks: t('Stock situation'),
        movements: t('Movements'),
        orders: t('Orders'),
        references: t('References'),
        'additional-requests': t('Additional requests'),
        invoices: t('Invoices'),
        others: t('Others'),
    };
}

export function BreadcrumbViewLabel({ match }) {
    const { data, isLoading } = useQuery({
        queryKey: ['saved-filters', match.params.viewId],
        queryFn: () => call(`saved-filters/${match.params.viewId}`),
        enabled: !!match.params.viewId,
    });

    if (isLoading) {
        return;
    }

    const name = data?.name;
    if (name) {
        return <>{name}</>;
    } else {
        return <>{match.params.viewId}</>;
    }
}

export function BreadcrumbOrderLabel({ match }) {
    const orderId = match.params.orderId || match.params.saleId;
    const { data, isLoading, error } = useQuery({
        queryKey: ['orders', orderId],
        queryFn: () => call(`orders/${orderId}`),
        enabled: validateUUID(orderId),
    });

    useReloadPageAndClearStorageError(error);

    if (isLoading) {
        return;
    }
    if (data?.shipperOrderReference) {
        return <>{data?.shipperOrderReference}</>;
    } else {
        return <>{orderId}</>;
    }
}

export function BreadcrumbTransportLabel({ match }) {
    const orderId = match.params.orderId;

    const { data, isLoading } = useQuery({
        queryKey: [`orders/${orderId}/getTransport`, orderId],
        queryFn: async () => await call(`orders/${orderId}/getTransport`),
    });

    if (isLoading) {
        return;
    }
    if (data?.transportOrderReference) {
        return <>{data?.transportOrderReference}</>;
    } else {
        return <>{orderId}</>;
    }
}

export function BreadcrumCarrierLabel({ match }) {
    const carrierId = match.params.carrierId;

    const { data, isLoading } = useQuery({
        queryKey: [`customer-carriers/${carrierId}`, carrierId],
        queryFn: async () => await call(`customer-carriers/${carrierId}`),
    });

    if (isLoading) {
        return;
    }
    if (data?.carrier?.company) {
        return <>{data?.carrier.company}</>;
    } else {
        return <>{carrierId}</>;
    }
}

export function BreadcrumbAppointmentLabel({ match }) {
    const appointmentId = match.params.appointmentId;

    const { data, isLoading, error } = useQuery({
        queryKey: ['appointments', appointmentId],
        queryFn: () => call(`appointments/${appointmentId}`),
        enabled: !!appointmentId && appointmentId !== 'edit',
    });

    useReloadPageAndClearStorageError(error);

    if (match.params.appointmentId === 'edit') {
        return;
    }

    if (isLoading) {
        return;
    }

    const order = data?.transports?.[0]?.order || data?.transport?.order;
    const title = order?.shipperOrderReference || data?.extOrderReference;

    if (title) {
        return <>{title}</>;
    } else {
        return <>{appointmentId}</>;
    }
}

export function BreadcrumbTourLabel({ match }) {
    const tourId = match.params.tourId;

    const { data, isLoading } = useQuery({
        queryKey: ['/tours/get-tour-detail', tourId],
        queryFn: () => call('/tours/get-tour-detail', { publicId: tourId }, 'POST'),
        enabled: validateUUID(tourId),
    });

    if (isLoading) {
        return;
    }
    if (data?.tourReference) {
        return <>{data.tourReference}</>;
    } else {
        return <>{tourId}</>;
    }
}

export function BreadcrumbAutomaticDataCompletionRuleLabel({ match }) {
    const ruleId = match.params.ruleId;
    const { data, isLoading, error } = useQuery({
        queryKey: [UPDATING_RULES_SEARCH_URL],
        queryFn: () => call(UPDATING_RULES_SEARCH_URL, {}, 'POST'),
        enabled: Boolean(ruleId) && ruleId !== 'new',
    });

    if (isLoading || error) {
        return;
    }

    const rules = Array.isArray(data) ? data : [];
    const rule = rules.find((item) => String(item.id) === String(ruleId));

    return <>{rule?.name || ruleId}</>;
}

export function BreadcrumbTabLabel({ match }) {
    const { t } = useTranslation();
    const labels = getTabLabels(t);

    return <>{labels[match.params.tab] ?? match.params.tab}</>;
}

// On order pages, the `references` tab id/hash surfaces as the "Content" tab — unlike the
// `references` tab on the documents page, which is really about references.
export function BreadcrumbOrderTabLabel({ match }) {
    const { t } = useTranslation();
    const labels = { ...getTabLabels(t), references: t('Content') };

    return <>{labels[match.params.tab] ?? match.params.tab}</>;
}

export function LoadableBreadcrumbItem({ loaded: loadedText, loading: loadingText }, bcType) {
    const [bcText, testId] = loadedText ? [loadedText, `${bcType}-loaded`] : [loadingText, `${bcType}-loading`];
    return <span data-testid={testId}>{bcText}</span>;
}
